/**
 * @zendir/ui - SensorFootprintCard Component
 *
 * Camera/Sensor Field of View visualization showing ground footprint.
 *
 * Features:
 * - Full null-safety with graceful fallbacks
 * - Canvas-based footprint visualization
 * - Coverage area calculations
 * - Loading and empty states
 */

import React, { memo, useMemo, useRef, useEffect } from "react";
import { useTheme } from "../theme";
import { AstroIcon, type AstroIconName } from "../core/display/AstroIcon";
import { classNames } from "../utils";

export interface FootprintSensorConfig {
  id: string;
  name: string;
  /** Field of view in degrees (cross-track) */
  fovDegrees: number;
  /** Along-track field of view in degrees */
  alongTrackFovDegrees?: number;
  /** Sensor type */
  type: "camera" | "radar" | "lidar" | "spectrometer" | string;
  /** Whether sensor is active */
  isActive: boolean;
}

export interface SensorFootprintCardProps {
  /** Sensor configuration */
  sensor?: FootprintSensorConfig;
  /** Current spacecraft latitude */
  latitude?: number;
  /** Current spacecraft longitude */
  longitude?: number;
  /** Current altitude in km */
  altitudeKm?: number;
  /** Current heading/track angle in degrees */
  headingDegrees?: number;
  /** Compact mode */
  compact?: boolean;
  /** Loading state */
  loading?: boolean;
  /** Custom class name */
  className?: string;
}

export const SensorFootprintCard = memo(function SensorFootprintCard({
  sensor,
  latitude,
  longitude,
  altitudeKm,
  headingDegrees = 0,
  compact = false,
  loading = false,
  className = "",
}: SensorFootprintCardProps): React.ReactElement {
  const { tokens, theme } = useTheme();
  const isTransparentTheme =
    theme === "transparent" ||
    theme === "transparent-bold" ||
    theme === "transparent-minimal";
  const cardBg = isTransparentTheme
    ? "transparent"
    : tokens.colors.background.surface;
  const cardGlass = isTransparentTheme
    ? {
        backdropFilter: "blur(12px)" as const,
        WebkitBackdropFilter: "blur(12px)" as const,
      }
    : {};
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const statusColors = useMemo(
    () => ({
      standby: tokens.colors.status.standby,
      off: tokens.colors.status.off,
      caution: tokens.colors.status.caution,
    }),
    [tokens],
  );

  const safeAltitude = altitudeKm ?? 400;
  const footprint = useMemo(() => {
    const fov = sensor?.fovDegrees ?? 30;
    const alongFov = sensor?.alongTrackFovDegrees ?? fov;
    const halfFovRad = (fov / 2) * (Math.PI / 180);
    const alongHalfFovRad = (alongFov / 2) * (Math.PI / 180);
    const crossTrackKm = 2 * safeAltitude * Math.tan(halfFovRad);
    const alongTrackKm = 2 * safeAltitude * Math.tan(alongHalfFovRad);
    const areaKm2 = crossTrackKm * alongTrackKm;
    const swathWidthKm = crossTrackKm;
    return { crossTrackKm, alongTrackKm, areaKm2, swathWidthKm };
  }, [sensor?.fovDegrees, sensor?.alongTrackFovDegrees, safeAltitude]);

  useEffect(() => {
    if (loading || !sensor) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const w = canvas.width;
    const h = canvas.height;
    ctx.fillStyle = tokens.colors.background.base;
    ctx.fillRect(0, 0, w, h);
    ctx.strokeStyle = tokens.colors.border.muted;
    ctx.lineWidth = 0.5;
    for (let x = 0; x <= w; x += w / 8) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, h);
      ctx.stroke();
    }
    for (let y = 0; y <= h; y += h / 6) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(w, y);
      ctx.stroke();
    }
    const cx = w / 2;
    const cy = h / 2;
    const scaleKmPerPixel = 1000 / Math.min(w, h);
    const halfWidth = footprint.crossTrackKm / 2 / scaleKmPerPixel;
    const halfHeight = footprint.alongTrackKm / 2 / scaleKmPerPixel;
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate((headingDegrees * Math.PI) / 180);
    ctx.fillStyle = sensor.isActive
      ? `${statusColors.standby}20`
      : `${statusColors.off}15`;
    ctx.beginPath();
    ctx.ellipse(0, 0, halfWidth, halfHeight, 0, 0, 2 * Math.PI);
    ctx.fill();
    ctx.strokeStyle = sensor.isActive ? statusColors.standby : statusColors.off;
    ctx.lineWidth = 2;
    ctx.setLineDash(sensor.isActive ? [] : [5, 5]);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.strokeStyle = `${tokens.colors.text.primary}40`;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(-halfWidth, 0);
    ctx.lineTo(halfWidth, 0);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(0, -halfHeight);
    ctx.lineTo(0, halfHeight);
    ctx.stroke();
    ctx.restore();
    ctx.fillStyle = tokens.colors.status.caution;
    ctx.beginPath();
    ctx.arc(cx, cy, 4, 0, 2 * Math.PI);
    ctx.fill();
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate((headingDegrees * Math.PI) / 180);
    ctx.fillStyle = tokens.colors.status.caution;
    ctx.beginPath();
    ctx.moveTo(0, -8);
    ctx.lineTo(3, -4);
    ctx.lineTo(-3, -4);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
    const scaleBarLength = 100;
    const scaleBarPixels = scaleBarLength / scaleKmPerPixel;
    ctx.fillStyle = tokens.colors.text.tertiary;
    ctx.fillRect(10, h - 20, scaleBarPixels, 3);
    ctx.font = "10px monospace";
    ctx.fillText(`${scaleBarLength} km`, 10, h - 8);
  }, [loading, sensor, tokens, footprint, headingDegrees, statusColors]);

  // Loading state
  if (loading) {
    return (
      <div
        className={classNames(
          "zendir-sensor-footprint-card",
          "loading",
          className,
        )}
        role="article"
        aria-busy="true"
        aria-label="Loading sensor data"
        style={{
          backgroundColor: cardBg,
          ...cardGlass,
          border: "none",
          borderRadius: tokens.borderRadius.lg,
          padding: tokens.spacing.md,
          minHeight: 200,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: 16,
          }}
        >
          <div
            style={{
              height: 24,
              width: "50%",
              backgroundColor: "rgba(255,255,255,0.1)",
              borderRadius: 4,
            }}
          />
          <div
            style={{
              height: 24,
              width: 60,
              backgroundColor: "rgba(255,255,255,0.1)",
              borderRadius: 4,
            }}
          />
        </div>
        <div
          style={{
            height: 120,
            backgroundColor: "rgba(255,255,255,0.06)",
            borderRadius: 8,
          }}
        />
      </div>
    );
  }

  // Empty state
  if (!sensor) {
    return (
      <div
        className={classNames(
          "zendir-sensor-footprint-card",
          "empty",
          className,
        )}
        role="article"
        aria-label="No sensor data"
        style={{
          backgroundColor: cardBg,
          ...cardGlass,
          border: "none",
          borderRadius: tokens.borderRadius.lg,
          padding: tokens.spacing.lg,
          textAlign: "center",
          color: tokens.colors.text.tertiary,
        }}
      >
        <AstroIcon
          name="camera"
          size="normal"
          label=""
          style={{ opacity: 0.5 }}
        />
        <p
          style={{
            margin: "8px 0 0 0",
            fontSize: tokens.typography.body[2].fontSize,
          }}
        >
          No sensor data available
        </p>
      </div>
    );
  }

  // Sensor type icon (AstroIcon)
  const getSensorIconName = (type: string): AstroIconName => {
    switch (type) {
      case "camera":
        return "camera";
      case "radar":
        return "antenna";
      case "lidar":
        return "flash-on";
      case "spectrometer":
        return "filter";
      default:
        return "image";
    }
  };

  // Format area (Astro: tabular numbers)
  const formatArea = (km2: number): string => {
    if (km2 >= 1e6) return `${(km2 / 1e6).toFixed(1)}M km²`;
    if (km2 >= 1e3) return `${(km2 / 1e3).toFixed(1)}k km²`;
    return `${km2.toFixed(0)} km²`;
  };

  const canvasSize = compact ? 120 : 180;

  // Astro UX compliant card styling
  const cardStyle: React.CSSProperties = {
    backgroundColor: cardBg,
    ...(isTransparentTheme && {
      backdropFilter: "blur(12px)" as const,
      WebkitBackdropFilter: "blur(12px)" as const,
    }),
    border: "none",
    borderRadius: tokens.borderRadius.lg,
    padding: tokens.spacing.md,
    fontFamily: tokens.typography.fontFamily.primary,
    color: tokens.colors.text.primary,
  };

  return (
    <div className={className} style={cardStyle}>
      {!compact && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: tokens.spacing.md,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: tokens.spacing.sm,
            }}
          >
            <AstroIcon
              name={getSensorIconName(sensor.type)}
              size="small"
              label={sensor.type}
              style={{ fontSize: tokens.typography.fontSize.lg }}
            />
            <div>
              {/* Astro: Sentence-case labels */}
              <h3
                style={{
                  fontWeight: tokens.typography.fontWeight.semibold,
                  color: tokens.colors.text.primary,
                  fontSize: tokens.typography.fontSize.md,
                  margin: 0,
                }}
              >
                {sensor.name}
              </h3>
              <p
                style={{
                  color: tokens.colors.text.tertiary,
                  fontSize: tokens.typography.fontSize.xs,
                  margin: 0,
                  marginTop: tokens.spacing.xs,
                }}
              >
                FOV: {sensor.fovDegrees}°
              </p>
            </div>
          </div>
          {/* Status badge - Astro style (sentence case per Tier 3) */}
          <span
            style={{
              padding: `${tokens.spacing.xs} ${tokens.spacing.sm}`,
              borderRadius: tokens.borderRadius.sm,
              fontSize: tokens.typography.fontSize.xxs, // 0.625rem / 10px (AstroUXDS compact)
              fontWeight: tokens.typography.fontWeight.bold,
              letterSpacing: "0.5px",
              backgroundColor: sensor.isActive
                ? `${statusColors.standby}20`
                : `${statusColors.off}20`,
              color: sensor.isActive ? statusColors.standby : statusColors.off,
            }}
          >
            {sensor.isActive ? "Active" : "Standby"}
          </span>
        </div>
      )}

      {/* Canvas visualization */}
      <div style={{ display: "flex", justifyContent: "center" }}>
        <canvas
          ref={canvasRef}
          width={canvasSize}
          height={canvasSize}
          style={{ borderRadius: tokens.borderRadius.md }}
        />
      </div>

      {/* Metrics - Astro: Sentence-case, tabular numbers */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(2, 1fr)",
          gap: tokens.spacing.sm,
          marginTop: tokens.spacing.md,
          fontSize: compact ? "10px" : tokens.typography.fontSize.xs,
        }}
      >
        <div
          style={{
            backgroundColor: tokens.colors.background.base,
            borderRadius: tokens.borderRadius.md,
            padding: tokens.spacing.sm,
            textAlign: "center",
          }}
        >
          <div
            style={{
              color: statusColors.standby,
              fontWeight: tokens.typography.fontWeight.bold,
              fontFamily: tokens.typography.fontFamily.mono,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {footprint.swathWidthKm.toFixed(0)} km
          </div>
          <div style={{ color: tokens.colors.text.tertiary }}>Swath width</div>
        </div>
        <div
          style={{
            backgroundColor: tokens.colors.background.base,
            borderRadius: tokens.borderRadius.md,
            padding: tokens.spacing.sm,
            textAlign: "center",
          }}
        >
          <div
            style={{
              color: statusColors.standby,
              fontWeight: tokens.typography.fontWeight.bold,
              fontFamily: tokens.typography.fontFamily.mono,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {formatArea(footprint.areaKm2)}
          </div>
          <div style={{ color: tokens.colors.text.tertiary }}>Coverage</div>
        </div>
      </div>

      {/* Position info - Astro: Sentence-case, tabular numbers */}
      {!compact && (
        <div
          style={{
            marginTop: tokens.spacing.md,
            paddingTop: tokens.spacing.md,
            borderTop: `1px solid ${tokens.colors.border.muted}`,
            fontSize: tokens.typography.fontSize.xs,
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span style={{ color: tokens.colors.text.tertiary }}>
              Nadir point
            </span>
            <span
              style={{
                fontFamily: tokens.typography.fontFamily.mono,
                color: tokens.colors.text.secondary,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {Math.abs(latitude ?? 0).toFixed(2)}°
              {(latitude ?? 0) >= 0 ? "N" : "S"},
              {Math.abs(longitude ?? 0).toFixed(2)}°
              {(longitude ?? 0) >= 0 ? "E" : "W"}
            </span>
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: tokens.spacing.xs,
            }}
          >
            <span style={{ color: tokens.colors.text.tertiary }}>Altitude</span>
            <span
              style={{
                fontFamily: tokens.typography.fontFamily.mono,
                color: tokens.colors.text.secondary,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {(altitudeKm ?? 0).toFixed(1)} km
            </span>
          </div>
        </div>
      )}
    </div>
  );
});
